export interface HedgePosition {
  ticker: string;
  stockName: string;
  quantity: number;
  currentPrice: number;
  totalValue: number;
  localCurrency: string;
  gainPct: number;
  initialInvestment: number;
  brPerformance?: number;  // BR column value (% performance contributing to margin)
}

export interface MarginData {
  marginRaw: number;       // M275 raw value
  portfolioValue: number;  // M163 raw value
  marginRatio: number;     // M275 / M163
  marginRatioPct: number;  // ratio as percentage
}

export interface ProtectivePut {
  ticker: string;
  contractsNeeded: number;
  strikePrice: number;        // ATM or slightly OTM
  estimatedPremium: number;   // per share
  totalCost: number;
  maxLoss: number;            // with hedge
  breakEvenPrice: number;
  protectionLevel: string;    // '95%', '90%', etc.
}

export interface CollarStrategy {
  ticker: string;
  contractsNeeded: number;
  putStrike: number;
  callStrike: number;
  netPremium: number;         // net cost (positive = debit, negative = credit)
  maxLoss: number;
  maxGain: number;
  protectionFloor: number;
  gainCap: number;
}

export interface CoveredCall {
  ticker: string;
  contractsNeeded: number;
  strikePrice: number;
  estimatedPremium: number;
  incomeGenerated: number;
  maxGain: number;
  downsideBuffer: number;     // % cushion from premium
}

export interface HedgeAnalysis {
  position: HedgePosition;
  protectivePuts: ProtectivePut[];
  collar: CollarStrategy;
  coveredCall: CoveredCall;
  portfolioWeight: number;
  riskScore: number;          // 1-10 based on gain exposure
}

// Estimate implied volatility proxy from gain% (simplified)
export function estimateVolatility(gainPct: number): number {
  const absGain = Math.abs(gainPct);
  if (absGain > 50) return 0.45;
  if (absGain > 30) return 0.35;
  if (absGain > 15) return 0.28;
  return 0.22;
}

// Simplified Black-Scholes-inspired premium estimate
export function estimateOptionPremium(
  spotPrice: number,
  strikePrice: number,
  volatility: number,
  daysToExpiry: number,
  type: 'put' | 'call'
): number {
  const T = daysToExpiry / 365;
  const intrinsic = type === 'put'
    ? Math.max(strikePrice - spotPrice, 0)
    : Math.max(spotPrice - strikePrice, 0);
  const timeValue = spotPrice * volatility * Math.sqrt(T) * 0.4;
  return Math.max(intrinsic + timeValue, 0.01);
}

export function calculateProtectivePut(
  position: HedgePosition,
  protectionPct: number,  // e.g., 0.95 for 95% protection
  daysToExpiry: number = 90
): ProtectivePut {
  const vol = estimateVolatility(position.gainPct);
  const strikePrice = position.currentPrice * protectionPct;
  const premium = estimateOptionPremium(position.currentPrice, strikePrice, vol, daysToExpiry, 'put');
  const contracts = Math.ceil(position.quantity / 100);
  const totalCost = premium * contracts * 100;
  const maxLoss = (position.currentPrice - strikePrice) * position.quantity + totalCost;
  const breakEven = position.currentPrice + premium;

  return {
    ticker: position.ticker,
    contractsNeeded: contracts,
    strikePrice: Math.round(strikePrice * 100) / 100,
    estimatedPremium: Math.round(premium * 100) / 100,
    totalCost: Math.round(totalCost * 100) / 100,
    maxLoss: Math.round(maxLoss * 100) / 100,
    breakEvenPrice: Math.round(breakEven * 100) / 100,
    protectionLevel: `${Math.round(protectionPct * 100)}%`,
  };
}

export function calculateCollar(
  position: HedgePosition,
  putProtectionPct: number = 0.95,
  callCapPct: number = 1.10,
  daysToExpiry: number = 90
): CollarStrategy {
  const vol = estimateVolatility(position.gainPct);
  const putStrike = position.currentPrice * putProtectionPct;
  const callStrike = position.currentPrice * callCapPct;
  const putPremium = estimateOptionPremium(position.currentPrice, putStrike, vol, daysToExpiry, 'put');
  const callPremium = estimateOptionPremium(position.currentPrice, callStrike, vol, daysToExpiry, 'call');
  const contracts = Math.ceil(position.quantity / 100);
  const netPremium = (putPremium - callPremium) * contracts * 100;
  const maxLoss = (position.currentPrice - putStrike) * position.quantity + Math.max(netPremium, 0);
  const maxGain = (callStrike - position.currentPrice) * position.quantity - Math.max(netPremium, 0);

  return {
    ticker: position.ticker,
    contractsNeeded: contracts,
    putStrike: Math.round(putStrike * 100) / 100,
    callStrike: Math.round(callStrike * 100) / 100,
    netPremium: Math.round(netPremium * 100) / 100,
    maxLoss: Math.round(maxLoss * 100) / 100,
    maxGain: Math.round(maxGain * 100) / 100,
    protectionFloor: Math.round(putProtectionPct * 100),
    gainCap: Math.round(callCapPct * 100),
  };
}

export function calculateCoveredCall(
  position: HedgePosition,
  callCapPct: number = 1.10,
  daysToExpiry: number = 30
): CoveredCall {
  const vol = estimateVolatility(position.gainPct);
  const strikePrice = position.currentPrice * callCapPct;
  const premium = estimateOptionPremium(position.currentPrice, strikePrice, vol, daysToExpiry, 'call');
  const contracts = Math.floor(position.quantity / 100);
  const income = premium * contracts * 100;
  const maxGain = (strikePrice - position.currentPrice) * position.quantity + income;
  const bufferPct = (premium / position.currentPrice) * 100;

  return {
    ticker: position.ticker,
    contractsNeeded: contracts,
    strikePrice: Math.round(strikePrice * 100) / 100,
    estimatedPremium: Math.round(premium * 100) / 100,
    incomeGenerated: Math.round(income * 100) / 100,
    maxGain: Math.round(maxGain * 100) / 100,
    downsideBuffer: Math.round(bufferPct * 100) / 100,
  };
}

export function analyzeHedge(
  position: HedgePosition,
  totalPortfolioValue: number
): HedgeAnalysis {
  const weight = position.totalValue / totalPortfolioValue;
  const riskScore = Math.min(10, Math.max(1, Math.round(
    (Math.abs(position.gainPct) * weight * 20) + (weight * 10)
  )));

  return {
    position,
    protectivePuts: [
      calculateProtectivePut(position, 1.00, 90),   // ATM
      calculateProtectivePut(position, 0.95, 90),   // 5% OTM
      calculateProtectivePut(position, 0.90, 90),   // 10% OTM
    ],
    collar: calculateCollar(position),
    coveredCall: calculateCoveredCall(position),
    portfolioWeight: Math.round(weight * 10000) / 100,
    riskScore,
  };
}
